import React, { useEffect, useState, ReactElement } from "react";

import { CognitoUser, CognitoUserSession } from "amazon-cognito-identity-js";
import Amplify, { Auth } from "aws-amplify";

import { AmplifyConfig } from "amplifyConfig";
import { isBackdoorEnabled } from "api_clients/backdoor";

const stub = (): never => {
  throw new Error(
    "You forgot to wrap your component in <CognitoAuthProvider>."
  );
};

export interface AuthState {
  isAuthenticated: boolean;
  isInitialized: boolean;
  provider?: string;
  session?: CognitoUserSession;
  user?: CognitoUser;
}

const initialState: AuthState = {
  isAuthenticated: false,
  isInitialized: false,
};

export interface IAuthContext extends AuthState {
  signIn: (provider: string) => void;
  signOut: () => void;
  setIsAuthenticated: (isAuthenticated: boolean) => void;
  setProvider: (provider: string) => void;
  setSession: (session: CognitoUserSession) => void;
  setUser: (user: CognitoUser) => void;
  getSession: () => Promise<CognitoUserSession>;
}

const initialContext: IAuthContext = {
  ...initialState,
  signIn: stub,
  signOut: stub,
  setIsAuthenticated: stub,
  setProvider: stub,
  setSession: stub,
  setUser: stub,
  getSession: stub,
};

export const AuthContext = React.createContext<IAuthContext>(initialContext);

interface CognitoAuthProviderProps {
  amplifyConfig: AmplifyConfig;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  children: any;
}

const getRedirectURI = () => {
  const redirectUri = process.env.REACT_APP_COGNITO_REDIRECT_URI;
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  return redirectUri!;
};

const getProviderInSubdomain = () => {
  const redirectUri = getRedirectURI();
  const domain = redirectUri.split("://")[1];
  const domainWithSub = window.location.origin.split("://")[1];
  return domainWithSub.slice(domainWithSub.length * -1, domain.length * -1 - 1);
};

const getURLWithSubdomain = (subdomain: string) => {
  const domain = getRedirectURI();
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const partsOfDomain = domain!.split("://");
  return `${partsOfDomain[0]}://${subdomain}.${partsOfDomain[1]}`;
};

const redirectIfNeeded = (session: CognitoUserSession) => {
  const idToken = session.getIdToken();
  const authorizedIdentity = idToken.payload.identities[0];

  if (authorizedIdentity) {
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const redirectTo = getURLWithSubdomain(authorizedIdentity.providerName!);
    if (window.location.origin !== redirectTo) {
      window.location.href = redirectTo;
    }
  }
};

// eslint-disable-next-line import/no-default-export
export default function CognitoAuthProvider({
  amplifyConfig,
  children,
}: CognitoAuthProviderProps): ReactElement | null {
  Amplify.configure(amplifyConfig);

  const [isInitialized, setIsInitialized] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [provider, setProvider] = useState<string | undefined>(
    getProviderInSubdomain()
  );
  const [session, setSession] = useState<CognitoUserSession>();
  const [user, setUser] = useState<CognitoUser>();
  const [isSignInNeeded, setIsSignInNeeded] = useState<boolean>(false);
  const getSession = async (): Promise<CognitoUserSession> =>
    Auth.currentSession();

  const signIn = async (customProvider: string) => {
    if (!customProvider) return;

    await Auth.federatedSignIn({ customProvider });
    const redirectURI = getRedirectURI();
    if (window.location.origin === redirectURI) {
      const redirectURIWithSubdomain = getURLWithSubdomain(customProvider);
      window.location.href = redirectURIWithSubdomain;
    }
  };

  const signOut = async () => {
    try {
      setIsAuthenticated(false);
      await Auth.signOut();
    } catch (error) {
      // eslint-disable-next-line no-console
      console.log("error signing out: ", error);
    }
  };

  useEffect(() => {
    (async () => {
      if (isBackdoorEnabled()) {
        setIsAuthenticated(true);
        setIsInitialized(true);
      } else {
        try {
          const currentSession = await Auth.currentSession();
          redirectIfNeeded(currentSession);
          const currentUser = await Auth.currentAuthenticatedUser();
          if (currentSession && currentUser) {
            setSession(currentSession);
            setUser(currentUser);
            setIsAuthenticated(true);
            setIsInitialized(true);
          }
        } catch (_error) {
          setIsAuthenticated(false);
          if (getProviderInSubdomain()) {
            setIsSignInNeeded(true);
          } else {
            setIsInitialized(true);
          }
        }
      }
    })();
  }, []);

  useEffect(() => {
    (async () => {
      if (!isSignInNeeded) return;

      const customProvider = getProviderInSubdomain();
      if (!customProvider) return;
      await Auth.federatedSignIn({ customProvider });
    })();
  }, [isSignInNeeded]);

  return (
    <AuthContext.Provider
      // eslint-disable-next-line react/jsx-no-constructed-context-values
      value={{
        isAuthenticated,
        isInitialized,
        provider,
        session,
        user,
        signIn,
        signOut,
        setIsAuthenticated,
        setProvider,
        setSession,
        setUser,
        getSession,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}
